import { createStyles, makeStyles, Theme } from '@material-ui/core';
import { MarketplaceProduct } from 'api/admin/types';
import { FraudToolDetails } from 'features/help/types/fraudToolDetails';
import { Message } from 'features/help/types/messagingCollection';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { normalizeServiceName } from 'utilities/utilities';
import { FeatureDetailsTab, getFeatureDetailsTabItems } from './feature-details-tabs';
import { FeatureDetailsAboutCard } from './FeatureDetailsAboutCard';
import { ConfigurationListItem } from './ConfigurationUnderwriterList';
import { FeatureDetailsConfigurationCard } from './FeatureDetailsConfigurationCard';
import { TabbedContent } from './TabbedContent';
import TitleMonitoringConfiguration from './titleMonitoring/TitleMonitoringConfiguration';
import { TitleMonitoringConfiguration as TitleMonitoringConfigurationState } from './titleMonitoring/useTitleMonitoringConfiguration';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    verticalSpacing: {
      marginBottom: theme.spacing(3),
    },
  }),
);

export interface FeatureDetailsCardsProps {
  showTabbedContent: boolean;
  selectedService: MarketplaceProduct;
  descriptionMessage?: Message | null;
  fraudToolDetails?: FraudToolDetails | null;
  isWireValidation: boolean;
  rapidResponsePdfUrl: string | null;
  onContactUsClick: () => void;
  isTitleMonitoring?: boolean;
  titleMonitoringConfiguration?: TitleMonitoringConfigurationState;
  configurationItems?: ConfigurationListItem[];
  onManageUnderwritersClick?: () => void;
  showUnderwriterUi?: boolean;
}

export const FeatureDetailsCards: FC<FeatureDetailsCardsProps> = ({
  showTabbedContent,
  selectedService,
  descriptionMessage,
  fraudToolDetails,
  isWireValidation,
  rapidResponsePdfUrl,
  onContactUsClick,
  isTitleMonitoring = false,
  titleMonitoringConfiguration,
  configurationItems = [],
  onManageUnderwritersClick,
  showUnderwriterUi = false,
}) => {
  const classes = useStyles();
  const [activeTabId, setActiveTabId] = useState<FeatureDetailsTab>(FeatureDetailsTab.About);
  const { productName, isEnabled } = selectedService;
  const wasEnabled = useRef(isEnabled);

  useEffect(() => {
    // Enabling Title Monitoring takes the user straight to the configuration they now need to fill in.
    const justEnabled = showTabbedContent && isTitleMonitoring && isEnabled && !wasEnabled.current;
    wasEnabled.current = isEnabled;
    setActiveTabId(justEnabled ? FeatureDetailsTab.Configuration : FeatureDetailsTab.About);
  }, [isEnabled, isTitleMonitoring, showTabbedContent]);

  const tabItems = useMemo(
    () =>
      getFeatureDetailsTabItems({
        testIdPrefix: isTitleMonitoring ? 'title-monitoring' : normalizeServiceName(productName),
        disableConfiguration: isTitleMonitoring && !isEnabled,
        useUnderwriterProductTestIds: showUnderwriterUi,
      }),
    [isEnabled, isTitleMonitoring, productName, showUnderwriterUi],
  );

  const aboutCardProps = {
    selectedService,
    descriptionMessage,
    fraudToolDetails,
    isWireValidation,
    rapidResponsePdfUrl,
    onContactUsClick,
  };

  const configurationContent =
    isTitleMonitoring && titleMonitoringConfiguration ? (
      <TitleMonitoringConfiguration configuration={titleMonitoringConfiguration} />
    ) : (
      <FeatureDetailsConfigurationCard
        isEnabled={isEnabled}
        items={configurationItems}
        onManageUnderwritersClick={onManageUnderwritersClick}
      />
    );

  if (!showTabbedContent) {
    return (
      <div className={classes.verticalSpacing}>
        <FeatureDetailsAboutCard {...aboutCardProps} />
      </div>
    );
  }

  return (
    <TabbedContent
      tabs={tabItems}
      activeTab={activeTabId}
      onTabChange={(tabId) => setActiveTabId(tabId as FeatureDetailsTab)}
      className={classes.verticalSpacing}
    >
      {activeTabId === FeatureDetailsTab.About ? <FeatureDetailsAboutCard {...aboutCardProps} /> : configurationContent}
    </TabbedContent>
  );
};
